import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { selectCustomStyles } from "../../../common/selectCustomStyles";

import {
    getSemesters,
    getCourseUnits,
    addNewEvaluation,
    updateEvaluation,
    getEvaluations,
    getSessions,
    getEvaluationTypes,
} from "store/actions";

import { Row, Col, Card, CardBody, Form, Label, Button, FormFeedback, Input } from "reactstrap";
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import { ToastContainer, toast } from "react-toastify";

import CourseUnitSelector from "./CourseUnitSelector";
import "./evaluationForm.css";

const EvaluationForm = () => {
    document.title = "Évaluation | CinzanEdu";

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { uuid } = useParams();

    const [isEdit, setIsEdit] = useState(false);
    const [selectedSemesters, setSelectedSemesters] = useState([]);
    const [filteredCourseUnits, setFilteredCourseUnits] = useState([]);
    const [selectedCourseUnitIds, setSelectedCourseUnitIds] = useState([]);

    const semesters = useSelector(state => state.semesterReducer.semesters);
    const courseUnits = useSelector(state => state.courseUnitReducer.courseUnits);
    const evaluations = useSelector(state => state.evaluationReducer.evaluations);
    const sessions = useSelector(state => state.sessionReducer.sessions);
    const evaluationTypes = useSelector(state => state.evaluationTypeReducer.evaluationTypes);

    useEffect(() => {
        if (!semesters.length) dispatch(getSemesters());
        if (!courseUnits.length) dispatch(getCourseUnits());
        if (!evaluations.length) dispatch(getEvaluations());
        if (!sessions.length) dispatch(getSessions());
        if (!evaluationTypes.length) dispatch(getEvaluationTypes());
    }, [dispatch, semesters.length, courseUnits.length, evaluations.length, evaluationTypes.length,  sessions.length]);

  const semesterOptions = semesters.map(s => ({ value: s.id, label: s.code }));

  // Initial Values Formik avec semestres et UE
  const [initialValues, setInitialValues] = useState({
    title: '',
    evaluation_type_id: '',
    session_id: '',
    start_date: '',
     end_date: '',
    //evaluation_time: '',
    semester_ids: [],
    course_unit_ids: []
  });

  // Mode édition : remplir initialValues + états locaux
  useEffect(() => {
    if (uuid && evaluations.length) {
      const evalToEdit = evaluations.find(e => e.uuid === uuid);
      if (evalToEdit) {
        

        const semester_ids = (evalToEdit.semesters || []).map(s => s.id);
        const course_unit_ids = (evalToEdit.course_units || []).map(cu => cu.id);

        console.log("SEMESTER FROM EVALUATION: ", semester_ids);

        setInitialValues({
          title: evalToEdit.title || '',
          session_id: evalToEdit.session_id || '',
          evaluation_type_id: evalToEdit.evaluation_type_id || '',
          start_date: evalToEdit.start_date || '',
          end_date: evalToEdit.end_date || '',
          //evaluation_time: evalToEdit.evaluation_time || '',
          semester_ids,
          course_unit_ids
        });

        setSelectedSemesters(semester_ids);
        setSelectedCourseUnitIds(course_unit_ids);
        setIsEdit(true);
      }
    }
  }, [uuid, evaluations]);

  // Filtrer dynamiquement les UE selon semestres sélectionnés
  useEffect(() => {
    if (!selectedSemesters.length) {
      setFilteredCourseUnits([]);
      return;
    }
    const filtered = courseUnits.filter(cu =>
      selectedSemesters.includes(Number(cu.semester_id))
    );
    setFilteredCourseUnits(filtered);
  }, [selectedSemesters, courseUnits]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      title: Yup.string().required("Le titre est obligatoire"),
      evaluation_type_id: Yup.string().required("Le type d'évaluation est obligatoire"),
      start_date: Yup.date().required("La date de début est obligatoire"),
      end_date: Yup.date().required("La date de fin est obligatoire"),
      //evaluation_time: Yup.string().required("L'heure est obligatoire"),
      semester_ids: Yup.array().min(1, "Veuillez sélectionner au moins un semestre"),
      course_unit_ids: Yup.array().min(1, "Veuillez sélectionner au moins une UE")
    }),
    onSubmit: (values) => {
      const payload = {
        ...values,
        evaluation_semester_ids: selectedSemesters,
        evaluation_course_unit_ids: selectedCourseUnitIds
      };

      if (isEdit) {
        dispatch(updateEvaluation({ ...payload, uuid }));
        toast.success("Évaluation mise à jour avec succès !");
      } else {
        dispatch(addNewEvaluation(payload));
        toast.success("Évaluation créée avec succès !");
      }

      navigate("/evaluations");
    }
  });

  // Toggle d'une UE
  const handleToggleUnit = (unit, checked) => {
    const id = Number(unit.id);
    const newSelectedIds = checked
      ? [...selectedCourseUnitIds, id]
      : selectedCourseUnitIds.filter(i => i !== id);

    setSelectedCourseUnitIds(newSelectedIds);
    formik.setFieldValue("course_unit_ids", newSelectedIds);
  };

  // Toggle "Tout cocher / Tout décocher"
  const handleToggleAll = (units, allSelected) => {
    const ids = units.map(u => Number(u.id));
    const newSelectedIds = allSelected
      ? selectedCourseUnitIds.filter(id => !ids.includes(id))
      : [...new Set([...selectedCourseUnitIds, ...ids])];

    setSelectedCourseUnitIds(newSelectedIds);
    formik.setFieldValue("course_unit_ids", newSelectedIds);
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs
          title="Évaluations"
          breadcrumbItem={isEdit ? "Modifier une évaluation" : "Nouvelle évaluation"}
        />

        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <Form onSubmit={formik.handleSubmit}>

                  {/* Titre et type */}
                  <Row className="mb-3">
                    <Col md="4">
                      <Label>Titre</Label>
                      <Input
                        name="title"
                        placeholder="Titre"
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        invalid={formik.touched.title && !!formik.errors.title}
                      />
                      <FormFeedback>{formik.errors.title}</FormFeedback>
                    </Col>
                    <Col md="4">
                      <Label>Type d'évaluation</Label>
                      <Input
                            name="evaluation_type_id"
                            type="select"
                            className="form-select"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.evaluation_type_id || ""}
                            invalid={formik.touched.evaluation_type_id && formik.errors.evaluation_type_id}
                        >
                            <option value="">-- Sélectionner --</option>
                            {evaluationTypes.map(s => (
                            <option key={s.id} value={s.id}>{s.title}</option>
                            ))}
                        </Input>
                        {formik.touched.evaluation_type_id && formik.errors.evaluation_type_id && (
                            <FormFeedback type="invalid">{formik.errors.evaluation_type_id}</FormFeedback>
                        )}
                    </Col>
                    <Col md="4">
                        <div className="mb-3">
                            <Label>Session</Label>
                            <Input
                                name="session_id"
                                type="select"
                                className="form-select"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.session_id || ""}
                                invalid={formik.touched.session_id && formik.errors.session_id}
                            >
                                <option value="">-- Sélectionner --</option>
                                {sessions.map(s => (
                                <option key={s.id} value={s.id}>{s.title}</option>
                                ))}
                            </Input>
                            {formik.touched.session_id && formik.errors.session_id && (
                                <FormFeedback type="invalid">{formik.errors.session_id}</FormFeedback>
                            )}
                        </div>
                    </Col>
                    
                  </Row>

                  {/* Date et heure */}
                  <Row className="mb-3">
                    <Col md="6">
                      <Label>Date début</Label>
                      <Input
                        type="date"
                        name="start_date"
                        value={formik.values.start_date}
                        onChange={formik.handleChange}
                        invalid={formik.touched.start_date && !!formik.errors.start_date}
                      />
                      <FormFeedback>{formik.errors.start_date}</FormFeedback>
                    </Col>
                    <Col md="6">
                      <Label>Date de fin</Label>
                      <Input
                        type="date"
                        name="end_date"
                        value={formik.values.end_date}
                        onChange={formik.handleChange}
                        invalid={formik.touched.end_date && !!formik.errors.end_date}
                      />
                      <FormFeedback>{formik.errors.end_date}</FormFeedback>
                    </Col>
                   
                  </Row>

                  {/* Semestres */}
                  <Row className="mb-3">
                    <Col md="12">
                      <Label>Semestres</Label>
                      <Select
                        options={semesterOptions}
                        isMulti
                        value={semesterOptions.filter(s => selectedSemesters.includes(s.value))}
                        onChange={(selected) => {
                          const values = selected ? selected.map(s => s.value) : [];
                          setSelectedSemesters(values);
                          formik.setFieldValue("semester_ids", values);
                        }}
                        styles={selectCustomStyles}
                      />
                      {formik.errors.semester_ids && <div className="text-danger mt-1">{formik.errors.semester_ids}</div>}
                    </Col>
                  </Row>

                  {/* Unités d’enseignement */}
                  <Row className="mb-3">
                    <Col md="12">
                      <Label>Unités d’enseignement</Label>
                      <div className="units-container">
                        {selectedSemesters.length === 0 && (
                          <div className="text-muted">Sélectionnez d’abord un semestre pour voir ses UE.</div>
                        )}

                        {selectedSemesters.map((semId) => {
                          const semester = semesters.find((s) => s.id === semId);
                          if (!semester) return <div key={semId} className="text-danger mb-2">Semestre introuvable (ID: {semId})</div>;

                          const units = filteredCourseUnits.filter((cu) => cu.semester_id === semId);
                          if (!units.length) return <div key={semId} className="text-muted mb-2">Pas d’UE disponibles pour le semestre {semester.code}</div>;

                          return (
                            <CourseUnitSelector
                              key={semId}
                              semester={semester}
                              units={units}
                              selectedUnitIds={selectedCourseUnitIds}
                              onToggleUnit={handleToggleUnit}
                              onToggleAll={handleToggleAll}
                            />
                          );
                        })}
                      </div>
                      {formik.errors.course_unit_ids && <div className="text-danger mt-1">{formik.errors.course_unit_ids}</div>}
                    </Col>
                  </Row>

                  <Button type="submit" color="primary">
                    {isEdit ? "Mettre à jour" : "Créer l'évaluation"}
                  </Button>

                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <ToastContainer />
      </div>
    </div>
  );
};

export default EvaluationForm;
